import { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch } from "react-redux";
import Container from "react-bootstrap/Container";
import DashboardWorksTable from "./DashboardWorksTable";
import useDashboardCreation from "./useDashboardCreation";
import { dashboardParties } from "./dashboardParties";
import { useNavigate } from "react-router-dom";
import { selectClient } from "../../data/store/actions/clientStoreFunctions";
import { selectWork } from "../../data/store/actions/workStoreFunctions";
import Loading from "../helpers/Loading";
import { getWorkById } from "../../data/getters";
import Chart from "chart.js/auto";
import { useLanguage } from "../helpers/LanguageContext";
import formatPrice from "../helpers/formatPrice";
import {
  fetchCreatedItemsForWork,
  fetchCreatedTablesForWork,
  fetchObjectsForWork,
  fetchWorkFilesForWork
} from "../../data/storeManager";
import { useSelector } from "react-redux";
import "../../css/UserDashboard.css";

function UserDashboard() {
  const dispatch = useDispatch();
  const workStatusChartRef = useRef(null);
  const paymentStatusChartRef = useRef(null);
  const [hoveredClientId, setHoveredClientId] = useState(null);
  const works = useSelector((state) => state.works);
  //const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workList, setWorkList] = useState([]);
  const navigate = useNavigate();
  const role = useSelector((state) => state.auth.user?.role);
  const isCompany = role === "companyUser" || role === "companyAdmin";
  const storedClients = useSelector((state) => state.clients);
  const users = useSelector((state) => state.auth.allUsers);
  const clients = useMemo(
    () =>
      dashboardParties(
        storedClients || [],
        users || [],
        works || [],
        isCompany
      ),
    [storedClients, users, works, isCompany]
  );
  const clientList = clients;
  const { t, language } = useLanguage();
  const isAdmin = role === "admin";
  const creation = useDashboardCreation({
    isCompany,
    isAdmin,
    parties: clients,
    t
  });

  // Kattintás eseménykezelő, ami a blokk láthatóságát váltja

  const translateStatus = (status) => {
    const key = status?.toLowerCase?.();
    return t(key) ?? status;
  };
  const clientCardStyle = (id) => ({
    minWidth: "clamp(160px, 30vw, 220px)",
    minHeight: "100px",
    margin: "10px",
    backgroundColor: "white",
    borderRadius: "15px",
    marginBottom: "15px",
    padding: "10px",
    position: "relative",
    cursor: "pointer",

    boxShadow:
      hoveredClientId === id
        ? "0 12px 28px rgba(0,0,0,.18), 0 4px 10px rgba(0,0,0,.08)"
        : "0 2px 6px rgba(0,0,0,.06)",
    transform: hoveredClientId === id ? "translateY(-2px)" : "translateY(0)",
    border:
      hoveredClientId === id
        ? "1px solid rgba(13,110,253,.35)"
        : "1px solid #eee",

    // gyorsabb és “snappier”
    transition:
      "transform 90ms cubic-bezier(0.2,0,0,1), box-shadow 110ms cubic-bezier(0.2,0,0,1), border-color 90ms",
    willChange: "transform, box-shadow" // mikro-lag ellen
  });

  const handleRowClick = async (workId) => {
    setLoading(true);
    try {
      await Promise.all([
        fetchObjectsForWork(workId),
        fetchCreatedItemsForWork(workId),
        fetchCreatedTablesForWork(workId),
        fetchWorkFilesForWork(workId)
      ]);
      const clientId = dispatch(getWorkById(workId))?.client?.clientId;
      if (clientId != null) dispatch(selectClient(clientId));
      dispatch(selectWork(workId));

      // 4) Navigálunk
      navigate(`/work/${workId}`); //navigate(`/workAnalyzer/${workId}`);
    } catch (error) {
      console.error("Error while selecting work:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setWorkList(works);
    setLoading(false);
  }, [clients, works]);

  const formatDate = (dateString) => {
    if (dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString(language); // Magyar nyelvi beállítás, vagy testre szabható
    }
    return "";
  };

  const handleSelectClient = async (clientId) => {
    setLoading(true);
    try {
      if (!isCompany) dispatch(selectClient(clientId));
      navigate(
        isCompany ? `/userAnalyzer/${clientId}` : `/clientAnalyzer/${clientId}`
      );
      setLoading(false);
    } catch (error) {
      console.error("Error while selecting client:", error);
      setLoading(false);
    }
  };
  /*
  const handleSelectWork = async (workId) => {
    setLoading(true);
    try {
      dispatch(selectWork(workId));
      fetchObjectsForWork(workId);
      fetchCreatedItemsForWork(workId);
      fetchCreatedTablesForWork(workId);
      const clientId = dispatch(getWorkById(workId))?.client?.clientId;
      if (clientId != null) dispatch(selectClient(clientId));
      navigate(`/workAnalyzer/${workId}`);
      setLoading(false);
    } catch (error) {
      console.error("Error while selecting work:", error);
      setLoading(false);
    }
  };*/

  /*  const handleDeleteWork = async (workId) => {
      await dispatch(workApi.deleteWorkApi(workId));
      rendering();
    };*/

  // Statistics calculation functions
  const calculateWorkStats = () => {
    const pending = works.filter((work) => work.status === "Pending").length;
    const completed = works.filter(
      (work) => work.status === "Completed"
    ).length;
    const measured = works.filter((work) => work.status === "Measured").length;
    const cancelled = works.filter(
      (work) => work.status === "Cancelled"
    ).length;
    const active = works.filter((work) => work.status === "Active").length;

    /*
      const pending = 8;
      const measured = 18;*/
    return { pending, measured, completed, cancelled, active };
  };

  const calculatePaymentStats = () => {
    const totalPaid = works.reduce((sum, work) => sum + work.paid, 0);
    const totalPrice = works.reduce((sum, work) => sum + work.price, 0);

    return { totalPaid, totalDue: totalPrice - totalPaid };
  };

  const workStats = calculateWorkStats();
  const paymentStats = calculatePaymentStats();

  useEffect(() => {
    if (workStatusChartRef.current) {
      const chartInstance = new Chart(workStatusChartRef.current, {
        type: "pie",
        data: {
          labels: ["Pending", "Measured", "Completed", "Cancelled", "Active"],
          datasets: [
            {
              data: [
                workStats.pending,
                workStats.measured,
                workStats.completed,
                workStats.cancelled,
                works.active
              ],
              backgroundColor: [
                "#FF6384",
                "#36A2EB",
                "#FFCD56",
                "#FF8A65",
                "#4BC0C0"
              ]
            }
          ]
        }
      });

      // Clean up chart when component unmounts
      return () => {
        chartInstance.destroy();
      };
    }
  }, [workStats, works]);

  useEffect(() => {
    if (paymentStatusChartRef.current) {
      const chartInstance = new Chart(paymentStatusChartRef.current, {
        type: "pie",
        options: {
          plugins: {
            tooltip: {
              callbacks: {
                label: (context) =>
                  `${context.label}: ${formatPrice(context.parsed)}`
              }
            }
          }
        },
        data: {
          labels: ["Paid", "Due"],
          datasets: [
            {
              data: [paymentStats.totalPaid, paymentStats.totalDue],
              backgroundColor: ["#4CAF50", "#FF9800"]
            }
          ]
        }
      });
      // Clean up chart when component unmounts
      return () => {
        chartInstance.destroy();
      };
    }
  }, [paymentStats]);

  return (
    <div className="user-dashboard">
      {loading && (
        <div className="dashboard-loading-overlay">
          <Loading />
        </div>
      )}
      {creation.modals}
      <div className="dashboard-layout dashboard-desktop">
        <div className="container d-xl-block">
          <div className="d-flex justify-content-between align-items-center">
            <p className="fs-1 fw-bold text-start mb-0 ">{t("dashboard")}</p>
          </div>
        </div>

        <Container className="d-xl-block pb-4">
          <div className="dashboard-party-header">
            <h2 className="fs-2 fw-bold text-start mb-0">
              {t(isCompany ? "users" : "clients")}
            </h2>
            {creation.actions}
          </div>
          {loading ? (
            <Loading />
          ) : clientList.length === 0 ? (
            <div
              style={{
                padding: "1rem",
                textAlign: "center",
                color: "#666",
                fontStyle: "italic"
              }}
            >
              {t(isCompany ? "noUsers" : "noClients")}
            </div>
          ) : (
            <div className="d-flex flex-nowrap overflow-x-scroll">
              {clients &&
                clientList.map((client) => (
                  <div
                    key={client.clientId}
                    style={clientCardStyle(client.clientId)}
                    onMouseEnter={() => setHoveredClientId(client.clientId)}
                    onMouseLeave={() => setHoveredClientId(null)}
                    onClick={() => handleSelectClient(client.clientId)}
                  >
                    <p className="fw-bold mb-2">{client.name}</p>
                    {isAdmin && client.carpenterName && (
                      <p className="small text-muted mb-2">
                        {client.carpenterName}
                      </p>
                    )}
                    <p className="mb-0">
                      {t(isCompany ? "orderedWorkCount" : "assignedWorkCount")}:{" "}
                      {client.workCount}
                    </p>
                  </div>
                ))}
            </div>
          )}
        </Container>

        <div className="container d-xl-block">
          <h2 className="fs-2 fw-bold text-start mb-3">{t("allWorksTitle")}</h2>
          <DashboardWorksTable
            works={workList}
            isCompany={isCompany}
            isAdmin={role === "admin"}
            users={users}
            t={t}
            formatDate={formatDate}
            translateStatus={translateStatus}
            onRowClick={handleRowClick}
          />
        </div>
      </div>
      <div className="dashboard-layout dashboard-tablet">
        <section className="dashboard-section">
          <div className="dashboard-party-header">
            <h2>{t(isCompany ? "users" : "clients")}</h2>
            {creation.actions}
          </div>

          <div className="tablet-client-table-wrapper">
            <table className="tablet-client-table">
              <thead>
                <tr>
                  <th>{t("name")}</th>
                  <th>
                    {t(isCompany ? "orderedWorkCount" : "assignedWorkCount")}
                  </th>
                </tr>
              </thead>

              <tbody>
                {clientList.map((client) => (
                  <tr
                    key={client.clientId}
                    onClick={() => handleSelectClient(client.clientId)}
                  >
                    <td>
                      <div>{client.name}</div>
                      {isAdmin && client.carpenterName && (
                        <small className="text-muted">
                          {client.carpenterName}
                        </small>
                      )}
                    </td>
                    <td>{client.workCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="dashboard-section">
          <h2>{t("allWorksTitle")}</h2>

          <DashboardWorksTable
            works={workList}
            isCompany={isCompany}
            isAdmin={role === "admin"}
            users={users}
            t={t}
            formatDate={formatDate}
            translateStatus={translateStatus}
            onRowClick={handleRowClick}
          />
        </section>
      </div>

      <div className="dashboard-layout dashboard-mobile">
        <section className="dashboard-section">
          <div className="dashboard-party-header">
            <h2>{t(isCompany ? "users" : "clients")}</h2>
            {creation.actions}
          </div>

          {clientList.map((client) => (
            <div
              key={client.clientId}
              className="mobile-client-card"
              onClick={() => handleSelectClient(client.clientId)}
            >
              <h3>{client.name}</h3>
              {isAdmin && client.carpenterName && (
                <p className="small text-muted mb-1">{client.carpenterName}</p>
              )}
              <p className="mb-0">
                {t(isCompany ? "orderedWorkCount" : "assignedWorkCount")}:{" "}
                {client.workCount}
              </p>
            </div>
          ))}
        </section>

        <section className="dashboard-section">
          <h2>{t("allWorksTitle")}</h2>

          <DashboardWorksTable
            works={workList}
            isCompany={isCompany}
            isAdmin={role === "admin"}
            users={users}
            t={t}
            formatDate={formatDate}
            translateStatus={translateStatus}
            onRowClick={handleRowClick}
          />
        </section>
      </div>
    </div>
  );
}

export default UserDashboard;
